import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { FormSelect } from '../../../../components/forms/FormSelect';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart, PatientDoc, FileTypes } from '@usacsmideo/types';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { PhotoCamera2 } from '../../../../components/PhotoCamera2';
import { FieldInfo } from '../../../../components/FieldInfo';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import QRCode from 'react-qr-code';

import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import usePatientDoc from '../../../../hooks/usePatientDoc';

export const Route = createFileRoute('/_authenticated/$patientId/info/govId')({
  component: IdSelf,
});
//TODO: MAKE THIS DYNAMIC BASED ON NAV TYPE
function IdSelf() {
  const navigate = useNavigate();
  const { mutate } = useUpdateChartSection();
  const [takingPhoto, setTakingPhoto] = React.useState({
    idFront: false,
    idBack: false,
    insuranceFront: false,
    insuranceBack: false,
    medicareFront: false,
    medicareBack: false,
    portrait: false,
    testDocument: false,
  });
  const { patientId } = Route.useParams();
  const linkedURI = `${import.meta.env.VITE_PORTAL_BASE_URL}/${patientId}/info/govId`;
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();

  const [warned, setWarned] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  // CONTINUE-OPTION CHOOSER (desktop only): 'browser' shows the upload form here,
  // 'mobile' shows the QR codes to continue on the mobile app. Default 'browser'
  // so desktop users see the upload form immediately (no blank panel).
  const [continueOption, setContinueOption] = React.useState<'browser' | 'mobile'>('browser');
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [photoTwoWorking, setPhotoTwoWorking] = React.useState(false);
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType?.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PHOTO_ID: {
            issuingState: idForm.state.values.issuingState,
            licenseNumber: idForm.state.values.idNumber,

            photoIdentificationFrontURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
            photoIdentificationBackURI: chart?.PHOTO_ID?.photoIdentificationBackURI
              ? chart.PHOTO_ID.photoIdentificationBackURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setIsSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error); // CAPTURE ERROR
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const idForm = useForm({
    defaultValues: {
      issuingState: chart?.PHOTO_ID?.issuingState ? chart.PHOTO_ID.issuingState : '',
      idNumber: chart?.PHOTO_ID?.licenseNumber ? chart?.PHOTO_ID.licenseNumber : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col items-center">
          <Heading id="photo-upload-page-header" textContent="Identification Upload" />
          <p className="px-2 text-xl">
            To get started, you will need to upload a copy of your government issued ID. MIDEO personnel will use this
            photo to match your identity with your MIDEO safety statement. We also perform an automated scan of this
            image to identify form information we can enter on your behalf. Choose one of the options below to upload
            your documents and continue the process.
          </p>
          {!isMobile && (
            <>
              <div className="my-2 w-full max-w-md">
                <FormSelect
                  label="How would you like to continue?"
                  id="continue-option-select"
                  name="continue-option"
                  value={continueOption}
                  required={false}
                  onBlur={() => {}}
                  onChange={(e) => setContinueOption(e.target.value as 'browser' | 'mobile')}
                  options={[
                    { name: 'Continue Here on Browser', value: 'browser' },
                    { name: 'Continue on Mobile Device', value: 'mobile' },
                  ]}
                />
              </div>
              {continueOption === 'mobile' && (
                <>
                  <p className="text-mideo-darkred mt-2 px-2 text-4xl">Continue on Mobile Device</p>
                  <div className="flex flex-row">
                    <div className="flex w-1/2 flex-col items-center justify-between px-4">
                      <p className="px-2 text-xl">
                        - Step 1: Download the MIDEO Health App by scanning QR code 1 and log in.
                      </p>
                      <QRCode
                        size={256}
                        style={{ height: 'auto', maxWidth: '50%', width: '100%' }}
                        value="http://qr.mideohealth.com/downloadmideo"
                        viewBox={`0 0 256 256`}
                      />
                      <p className="px-2 text-xs italic">QR Code 1</p>
                    </div>
                    <div className="flex w-1/2 flex-col items-center justify-between px-4">
                      <p className="items-center px-2 text-xl">
                        - Step 2: After logging in, scan QR code 2 to continue the process from your mobile device.
                      </p>
                      <QRCode
                        size={256}
                        style={{ height: 'auto', maxWidth: '50%', width: '100%' }}
                        value={linkedURI}
                        viewBox={`0 0 256 256`}
                      />
                      <p className="mt-2 px-2 text-xs italic">QR Code 2</p>
                    </div>
                  </div>
                </>
              )}
              {continueOption === 'browser' && (
                <p className="text-mideo-darkred mt-2 px-2 text-4xl">Continue Here on Browser</p>
              )}
            </>
          )}
          {/* UPLOAD FORM — shown on mobile devices, and on desktop only when 'browser' is chosen. */}
          {(isMobile || continueOption === 'browser') && (
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <p className="text-2xl font-bold">Government issued ID (front):</p>
              {chart?.PHOTO_ID?.photoIdentificationFrontURI && takingPhoto.idFront === false ? (
                <div className="flex w-full flex-row items-center justify-around">
                  <div id="photo-container" className="flex w-2/3 flex-col items-center p-4 lg:w-1/3">
                    <img src={chart?.PHOTO_ID?.photoIdentificationFrontURI} alt="ID Front" />
                    <p className="text-xl">Photo on File</p>
                  </div>
                  <div className="ml-2 flex flex-col items-center">
                    <Button
                      id="retake-gov-id-front"
                      type="button"
                      textContent="Retake Photo"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idFront: true });
                      }}
                    />
                  </div>
                </div>
              ) : (
                takingPhoto.idFront === false && (
                  <div className="flex w-full flex-row items-center justify-around">
                    <Button
                      id="take-gov-id-front"
                      type="button"
                      textContent="Take Photo"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idFront: true });
                      }}
                    />
                  </div>
                )
              )}
              {takingPhoto.idFront && (
                <PhotoCamera2
                  type={FileTypes.idFront}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationFront`}
                  patientId={patientId}
                  hideCamera={() => {
                    setTakingPhoto({ ...takingPhoto, idFront: false });
                    setPhotoOneWorking(false);
                  }}
                  working={photoOneWorking}
                  setWorking={setPhotoOneWorking}
                />
              )}
            </div>
            <div id="rear-id-container" className="flex flex-col items-center">
              <p className="text-2xl font-bold">Government Issued ID (rear):</p>
              {chart?.PHOTO_ID?.photoIdentificationBackURI && takingPhoto.idBack === false ? (
                <div className="flex w-full flex-row items-center justify-around">
                  <div id="photo-container" className="flex w-2/3 flex-col items-center p-4 lg:w-1/3">
                    <img src={chart?.PHOTO_ID?.photoIdentificationBackURI} alt="ID Back" />
                    <p className="text-xl">Photo on File</p>
                  </div>
                  <div className="mx-2 flex flex-col items-center">
                    <Button
                      id="retake-gov-id-back"
                      type="button"
                      textContent="Retake Photo"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idBack: true });
                      }}
                    />
                  </div>
                </div>
              ) : (
                takingPhoto.idBack === false && (
                  <div className="flex w-full flex-row items-center justify-around">
                    <Button
                      id="take-gov-id-back"
                      type="button"
                      textContent="Take Photo"
                      onClick={() => setTakingPhoto({ ...takingPhoto, idBack: true })}
                    />
                  </div>
                )
              )}
              {takingPhoto.idBack && (
                <PhotoCamera2
                  type={FileTypes.idBack}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationBack`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoTwoWorking(false);
                    setTakingPhoto({ ...takingPhoto, idBack: false });
                  }}
                  working={photoTwoWorking}
                  setWorking={setPhotoTwoWorking}
                />
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  idForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <idForm.Field
                    name="idNumber"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'ID Number cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="ID Number"
                          id="id-number"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <idForm.Field
                    name="issuingState"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Issuing state cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Issuing State"
                          id="issuing-state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
              </form>
              <div className="mt-8 flex flex-col items-center">
                <idForm.Subscribe
                  selector={(state) => [state.canSubmit, state.errors]}
                  children={([canSubmit]) => (
                    <>
                      {!canSubmit && (
                        <div className="mt-4 flex flex-col items-center">
                          <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                        </div>
                      )}
                      <Button
                        id="submit-id-info"
                        type="submit"
                        disabled={
                          !canSubmit ||
                          chart?.PHOTO_ID?.photoIdentificationFrontURI?.length < 5 ||
                          chart?.PHOTO_ID?.photoIdentificationBackURI?.length < 5
                        }
                        onClick={handleIdInfoUpdate}
                        textContent="Submit"
                        submitted={isSubmitted}
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
          )}
          <div id="nav" className="my-4 flex flex-col items-center">
            {/* {nextPageURL &&
              chart?.PHOTO_ID?.issuingState &&
              chart?.PHOTO_ID?.licenseNumber &&
              chart?.PHOTO_ID?.photoIdentificationBackURI &&
              chart?.PHOTO_ID?.photoIdentificationFrontURI && (
                <NavForward
                  to={nextPageURL}
                  disabled={
                    !isSubmitted ||
                    !chart?.PHOTO_ID?.issuingState ||
                    !chart?.PHOTO_ID?.licenseNumber ||
                    !chart?.PHOTO_ID?.photoIdentificationFrontURI ||
                    !chart?.PHOTO_ID?.photoIdentificationBackURI
                  }
                />
              )} */}
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={
                  !isSubmitted ||
                  !chart?.PHOTO_ID?.issuingState ||
                  !chart?.PHOTO_ID?.licenseNumber ||
                  !chart?.PHOTO_ID?.photoIdentificationFrontURI ||
                  !chart?.PHOTO_ID?.photoIdentificationBackURI
                }
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
